/**
 * Responsibility: Implement router behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// AI/ROUTER.TS — Multi-provider AI routing
// Defines the AIProvider interface and job-based routing.
// Providers: Gemini, OpenAI, Anthropic, Groq (cloud), Ollama (local)
// Job types determine which provider/model to prefer.
// ============================================================

import { recordAITelemetry, estimateTokensFromText, estimateTokensFromChars, getAITelemetry } from './telemetry'
import { emitTaskStart, emitTaskEnd } from './task-events'
import { readAdminRuntimeFlags } from '../admin/admin-contract'
import type { CommerceUsageMode } from '../commerce/commerce-contract'
import { recordManagedUsageFact, resolveCurrentEntitlement } from '../commerce/commerce-runtime'
import { resolveAIModelOverride } from './model-settings'
import { createAIError, formatAIErrorMessage, type AIProviderAttempt } from './errors'
export type JobType = 'genesis' | 'world_forge' | 'logic_forge' | 'narrator' | 'trait_shift' | 'world_tick' | 'world_enrich' | 'action_validator' | 'cheat' | 'music_forge' | 'preforge_draft' | 'image'
export interface AIRequest {
  jobType: JobType
  systemPrompt: string
  userPrompt: string
  model?: string; // optional model override per-request
  providerId?: string; // optional provider override per-request
}

export interface AIResponse<T> {
  data: T
  provider?: string; // which provider was used (for diagnostics)
  usageMode?: CommerceUsageMode
}

export interface AIProvider {
  readonly id: string
  readonly label: string
  isAvailable(): boolean | Promise<boolean>
  complete<T>(req: AIRequest): Promise<AIResponse<T>>
  streamText?(req: AIRequest): AsyncGenerator<string>
  listModels?(): Promise<string[]>
  getUsageMode?(): CommerceUsageMode | Promise<CommerceUsageMode>
}

function defaultUsageMode(providerId: string): CommerceUsageMode {
  if (providerId === 'mock' || providerId === 'ollama') return 'local'
  if (providerId === 'vertex-managed') return 'managed'
  return 'byok'
}

async function resolveUsageMode(provider: AIProvider, providerId: string, responseMode?: CommerceUsageMode): Promise<CommerceUsageMode> {
  if (responseMode) return responseMode
  if (provider.getUsageMode) return provider.getUsageMode()
  return defaultUsageMode(providerId)
}

async function isManagedAccessAllowed(id: string, provider: AIProvider): Promise<boolean> {
  const usageMode = await resolveUsageMode(provider, id)
  if (usageMode !== 'managed') return true
  const flags = readAdminRuntimeFlags()
  if (flags.managedAccessMode === 'open') return true
  const entitlement = await resolveCurrentEntitlement()
  if (flags.managedAccessMode === 'grants_only') return entitlement.source === 'admin'
  return entitlement.hostedProxyAccess
}

// Job type → provider preference order (first available wins).
// logic_forge: prefer local/fast — small structured task, saves cloud quota.
// creative jobs: prefer Gemini/Claude — prose quality matters.
const JOB_PREFERENCES: Record<JobType, string[]> = {
  logic_forge:  ['ollama', 'vertex-managed', 'groq', 'openai', 'gemini', 'anthropic', 'mock'],
  action_validator: ['ollama', 'vertex-managed', 'groq', 'openai', 'gemini', 'anthropic', 'mock'],
  narrator:     ['ollama', 'vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'mock'],
  genesis:      ['ollama', 'vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'mock'],
  world_forge:  ['ollama', 'vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'mock'],
  trait_shift:  ['ollama', 'groq', 'vertex-managed', 'gemini', 'openai', 'anthropic', 'mock'],
  world_tick:   ['ollama', 'vertex-managed', 'groq', 'openai', 'gemini', 'anthropic', 'mock'],
  world_enrich: ['ollama', 'vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'mock'],
  preforge_draft: ['ollama', 'vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'mock'],
  cheat:        ['ollama', 'vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'mock'],
  music_forge:  ['ollama', 'vertex-managed', 'groq', 'openai', 'gemini', 'anthropic', 'mock'],
  image:        ['gemini', 'stable-diffusion', 'mock'],
}

function resolveJobPreferences(jobType: JobType): string[] {
  const base = JOB_PREFERENCES[jobType] ?? JOB_PREFERENCES.logic_forge
  const override = readAdminRuntimeFlags().providerOverride
  if (!override || override === 'auto') return base
  return [override, ...base.filter(id => id !== override)]
}

function enforceLogicQuota(jobType: JobType): void {
  const quota = readAdminRuntimeFlags().logicQuotaPerHour
  if (!quota || quota <= 0) return
  if (!['logic_forge', 'world_tick', 'action_validator', 'trait_shift'].includes(jobType)) return
  const windowStart = Date.now() - (60 * 60 * 1000)
  const recentCount = getAITelemetry().filter(entry =>
    entry.ts >= windowStart
    && entry.ok
    && ['logic_forge', 'world_tick', 'action_validator', 'trait_shift'].includes(entry.jobType)
  ).length
  if (recentCount >= quota) {
    throw new Error(`[AIRouter] Admin logic quota reached (${quota}/hour).`)
  }
}

function resolveConfiguredRequest(req: AIRequest): { request: AIRequest; preference: string[]; isOverride: boolean } {
  const explicit = String(req.providerId ?? '').trim()
  if (explicit && explicit !== 'auto') {
    return { request: req, preference: [explicit], isOverride: false }
  }

  const override = resolveAIModelOverride(req.jobType)
  if (!override) {
    return { request: req, preference: resolveJobPreferences(req.jobType), isOverride: false }
  }

  const preference = [override.providerId, ...resolveJobPreferences(req.jobType).filter(id => id !== override.providerId)]
  return {
    request: {
      ...req,
      providerId: override.providerId,
      model: override.model,
    },
    preference,
    isOverride: true,
  }
}

function providerLabel(providerId: string): string {
  switch (providerId) {
    case 'vertex-managed': return 'Managed Cloud AI'
    case 'gemini': return 'Google Gemini'
    case 'openai': return 'OpenAI'
    case 'anthropic': return 'Anthropic Claude'
    case 'groq': return 'Groq'
    case 'ollama': return 'Ollama (Local)'
    case 'mock': return 'Mock provider'
    default: return providerId.toUpperCase()
  }
}

export class AIRouter {
  private providers = new Map<string, AIProvider>()
  private activeProviderId: string | null = null
  private activeProviderListeners = new Set<(providerId: string, providerLabel: string) => void>()
  register(provider: AIProvider): this {
    this.providers.set(provider.id, provider)
    return this
  }

  clearProviders(): void {
    this.providers.clear()
    this.activeProviderId = null
  }

  private markActiveProvider(providerId: string): void {
    if (!providerId || this.activeProviderId === providerId) return
    this.activeProviderId = providerId
    const label = this.providers.get(providerId)?.label ?? providerId.toUpperCase()
    for (const listener of this.activeProviderListeners) listener(providerId, label)
  }

  async route<T>(req: AIRequest): Promise<T> {
    const taskId = emitTaskStart(req.jobType)
    let ok = false
    const inputTokens = estimateTokensFromText(req.systemPrompt) + estimateTokensFromText(req.userPrompt)
    try {
      enforceLogicQuota(req.jobType)
      const resolved = resolveConfiguredRequest(req)
      const effectiveReq = resolved.request
      const preference = resolved.preference
      const attempts: AIProviderAttempt[] = []
      const explicit = String(effectiveReq.providerId ?? '').trim()
      if (explicit && explicit !== 'auto' && !resolved.isOverride) {
        const provider = this.providers.get(explicit)
        if (!provider) {
          throw createAIError('provider-not-registered', {
            jobType: req.jobType,
            providerId: explicit,
            providerLabel: providerLabel(explicit),
            model: String(effectiveReq.model ?? '').trim(),
            hint: 'Open AI settings and enable the provider, or switch the job to a configured model.',
          })
        }
        const available = await provider.isAvailable()
        if (!available) {
          throw createAIError('provider-unavailable', {
            jobType: req.jobType,
            providerId: explicit,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            hint: 'Check the API key, proxy URL, or local service health, then try again.',
          })
        }
        const allowed = await isManagedAccessAllowed(explicit, provider)
        if (!allowed) {
          throw createAIError('provider-disallowed', {
            jobType: req.jobType,
            providerId: explicit,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            hint: 'Switch to a BYOK or local provider, or update the current access mode.',
          })
        }
      }
      for (const id of preference) {
        const provider = this.providers.get(id)
        if (!provider) {
          attempts.push({
            providerId: id,
            providerLabel: providerLabel(id),
            model: String(effectiveReq.model ?? '').trim(),
            available: false,
            allowed: false,
            status: 'missing',
          })
          continue
        }
        const available = await provider.isAvailable()
        if (!available) {
          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed: false,
            status: 'unavailable',
          })
          continue
        }
        const allowed = await isManagedAccessAllowed(id, provider)
        if (!allowed) {
          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed,
            status: 'disallowed',
          })
          continue
        }
        const t0 = performance.now()
        const reqForProvider: AIRequest = (id === effectiveReq.providerId)
          ? effectiveReq
          : { ...effectiveReq, model: undefined, providerId: undefined }
          
        try {
          const resp = await provider.complete<T>(reqForProvider)
          const usageMode = await resolveUsageMode(provider, id, resp.usageMode)
          const durationMs = performance.now() - t0
          const outputTokens = estimateTokensFromChars(JSON.stringify(resp.data ?? '').length)

          console.log(`[AIRouter] ${id} / ${reqForProvider.model || 'default'} (${inputTokens + outputTokens} tokens) in ${(durationMs / 1000).toFixed(2)}s`)

          recordAITelemetry({
            jobType: req.jobType,
            provider: id,
            mode: 'route',
            durationMs,
            ok: true,
            inputTokens,
            outputTokens,
            usageMode,
          })

          await recordManagedUsageFact({
            kind: 'action',
            usageMode,
            provider: id,
            jobType: req.jobType,
            inputTokens,
            outputTokens: estimateTokensFromChars(JSON.stringify(resp.data ?? '').length),
          })
          this.markActiveProvider(id)
          ok = true
          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed,
            status: 'ok',
          })
          return resp.data
        } catch (err) {
          const durationMs = performance.now() - t0
          console.error(`[AIRouter] ${id} / ${reqForProvider.model || 'default'} failed in ${(durationMs / 1000).toFixed(2)}s:`, err)
          
          recordAITelemetry({
            jobType: req.jobType,
            provider: id,
            mode: 'route',
            durationMs,
            ok: false,
            error: String(err).slice(0, 140),
            inputTokens,
            outputTokens: 0,
          })

          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed,
            status: 'failed',
            error: formatAIErrorMessage(err).slice(0, 220),
          })
        }
      }

      throw createAIError('no-provider', {
        jobType: req.jobType,
        model: String(effectiveReq.model ?? '').trim(),
        attempts,
        hint: attempts.length
          ? 'See the provider list below and compare the selected model against the configured providers.'
          : 'Open AI settings and enable at least one provider for this job.',
      })
    } finally {
      emitTaskEnd(taskId, ok)
    }
  }

  async *stream(req: AIRequest): AsyncGenerator<string> {
    const taskId = emitTaskStart(req.jobType)
    let ok = false
    const inputTokens = estimateTokensFromText(req.systemPrompt) + estimateTokensFromText(req.userPrompt)
    try {
      enforceLogicQuota(req.jobType)
      const resolved = resolveConfiguredRequest(req)
      const effectiveReq = resolved.request
      const preference = resolved.preference
      const attempts: AIProviderAttempt[] = []
      const explicit = String(effectiveReq.providerId ?? '').trim()
      if (explicit && explicit !== 'auto' && !resolved.isOverride) {
        const provider = this.providers.get(explicit)
        if (!provider) {
          throw createAIError('provider-not-registered', {
            jobType: req.jobType,
            providerId: explicit,
            providerLabel: providerLabel(explicit),
            model: String(effectiveReq.model ?? '').trim(),
            hint: 'Open AI settings and enable the provider, or switch the job to a configured model.',
          })
        }
        const available = await provider.isAvailable()
        if (!available) {
          throw createAIError('provider-unavailable', {
            jobType: req.jobType,
            providerId: explicit,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            hint: 'Check the API key, proxy URL, or local service health, then try again.',
          })
        }
        const allowed = await isManagedAccessAllowed(explicit, provider)
        if (!allowed) {
          throw createAIError('provider-disallowed', {
            jobType: req.jobType,
            providerId: explicit,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            hint: 'Switch to a BYOK or local provider, or update the current access mode.',
          })
        }
      }
      for (const id of preference) {
        const provider = this.providers.get(id)
        if (!provider || !provider.streamText) {
          attempts.push({
            providerId: id,
            providerLabel: provider ? provider.label : providerLabel(id),
            model: String(effectiveReq.model ?? '').trim(),
            available: !!provider,
            allowed: false,
            status: !provider ? 'missing' : 'failed',
            error: !provider ? 'streaming provider not registered' : 'provider does not support streaming',
          })
          continue
        }
        const available = await provider.isAvailable()
        if (!available) {
          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed: false,
            status: 'unavailable',
          })
          continue
        }
        const allowed = await isManagedAccessAllowed(id, provider)
        if (!allowed) {
          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed,
            status: 'disallowed',
          })
          continue
        }
        const t0 = performance.now()
        const reqForProvider: AIRequest = (id === effectiveReq.providerId)
          ? effectiveReq
          : { ...effectiveReq, model: undefined, providerId: undefined }
          
        try {
          let streamChars = 0
          // Yield from the stream and then exit the loop on success
          for await (const chunk of provider.streamText(reqForProvider)) {
            streamChars += chunk.length
            yield chunk
          }
          const durationMs = performance.now() - t0
          const outputTokens = estimateTokensFromChars(streamChars)
          console.log(`[AIRouter] ${id} / ${reqForProvider.model || 'default'} (stream: ${inputTokens + outputTokens} tokens) in ${(durationMs / 1000).toFixed(2)}s`)
          
          recordAITelemetry({
            jobType: req.jobType,
            provider: id,
            mode: 'stream',
            durationMs,
            ok: true,
            inputTokens,
            outputTokens,
            usageMode: await resolveUsageMode(provider, id),
          })
          await recordManagedUsageFact({
            kind: 'action',
            usageMode: await resolveUsageMode(provider, id),
            provider: id,
            jobType: req.jobType,
            inputTokens,
            outputTokens: estimateTokensFromChars(streamChars),
          })
          this.markActiveProvider(id)
          ok = true
          return
        } catch (err) {
          const durationMs = performance.now() - t0
          console.error(`[AIRouter] streaming from ${id} / ${reqForProvider.model || 'default'} failed in ${(durationMs / 1000).toFixed(2)}s:`, err)
          
          recordAITelemetry({
            jobType: req.jobType,
            provider: id,
            mode: 'stream',
            durationMs,
            ok: false,
            error: String(err).slice(0, 140),
            inputTokens,
            outputTokens: 0,
          })
          attempts.push({
            providerId: id,
            providerLabel: provider.label,
            model: String(effectiveReq.model ?? '').trim(),
            available,
            allowed,
            status: 'failed',
            error: formatAIErrorMessage(err).slice(0, 220),
          })
        }
      }

      throw createAIError('no-provider', {
        jobType: req.jobType,
        model: String(effectiveReq.model ?? '').trim(),
        attempts,
        hint: attempts.length
          ? 'See the provider list below and compare the selected model against the configured providers.'
          : 'Open AI settings and enable at least one provider for this job.',
      })
    } finally {
      emitTaskEnd(taskId, ok)
    }
  }

  getProviders(): AIProvider[] {
    return [...this.providers.values()]
  }

  // Check availability of all providers (for status display)
  async getStatus(): Promise<Record<string, boolean>> {
    const status: Record<string, boolean> = {}
    await Promise.all(
      [...this.providers.entries()].map(async ([id, p]) => {
        status[id] = await p.isAvailable()
      }),
    )
    return status
  }

  getActiveProviderId(): string | null {
    return this.activeProviderId
  }

  getActiveProviderLabel(): string {
    const id = this.activeProviderId
    if (!id) return ''
    return this.providers.get(id)?.label ?? id.toUpperCase()
  }

  onActiveProviderChange(listener: (providerId: string, providerLabel: string) => void): () => void {
    this.activeProviderListeners.add(listener)
    return () => this.activeProviderListeners.delete(listener)
  }
}

// Singleton — configured by src/ai/config.ts
export const router = new AIRouter()
